import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  PAYMENT_TERMS,
  TAX_CLASSIFICATIONS,
  type Contact,
  type ContactType,
  type CreateContactInput,
  type TaxClassification,
} from '@accounting/shared';
import {
  useContacts,
  useCreateContact,
  useDeactivateContact,
  useUpdateContact,
} from '../../hooks/useContacts';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { ApiError } from '../../lib/api';

export interface ContactSectionProps {
  contactType: ContactType;
  titlePlural: string; // e.g. "Vendors"
  titleSingular: string; // e.g. "vendor"
}

/** A management page for one contact type (vendors, employees, or customers). */
export function ContactSection({ contactType, titlePlural, titleSingular }: ContactSectionProps) {
  const companyId = useActiveCompanyId();
  const hideKey = `accounting:contacts:${contactType}:hideInactive`;
  const [hideInactive, setHideInactive] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem(hideKey) !== 'false';
  });
  const [editing, setEditing] = useState<Contact | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const { data: contacts = [], isLoading, error } = useContacts(companyId, {
    includeInactive: !hideInactive,
    contactType,
  });

  const toggleHideInactive = (next: boolean) => {
    setHideInactive(next);
    window.localStorage.setItem(hideKey, next ? 'true' : 'false');
  };

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>{titlePlural}</h1>
        <p className="muted">No active company.</p>
        <Link to="/companies" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
          Go to Companies
        </Link>
      </div>
    );
  }

  const isVendor = contactType === 'vendor';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 24, margin: 0 }}>{titlePlural}</h1>
          <p className="muted" style={{ margin: '4px 0 0' }}>
            {contacts.length} {hideInactive ? 'active' : 'total'}
          </p>
        </div>
        {!showCreate && !editing && (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New {titleSingular}
          </button>
        )}
      </div>

      <label
        style={{
          display: 'inline-flex',
          gap: 6,
          alignItems: 'center',
          color: 'var(--text-muted)',
          fontSize: 14,
          cursor: 'pointer',
          userSelect: 'none',
          marginBottom: 16,
        }}
      >
        <input
          type="checkbox"
          checked={hideInactive}
          onChange={(e) => toggleHideInactive(e.target.checked)}
          style={{ margin: 0 }}
        />
        Hide inactive
      </label>

      {showCreate && (
        <ContactForm
          companyId={companyId}
          contactType={contactType}
          titleSingular={titleSingular}
          isVendor={isVendor}
          onClose={() => setShowCreate(false)}
        />
      )}

      {editing && (
        <ContactForm
          companyId={companyId}
          contactType={contactType}
          titleSingular={titleSingular}
          isVendor={isVendor}
          existing={editing}
          onClose={() => setEditing(null)}
        />
      )}

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load {titlePlural.toLowerCase()}.</p>}

      {!isLoading && contacts.length === 0 && !showCreate && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <p className="muted" style={{ marginTop: 0 }}>No {titlePlural.toLowerCase()} yet.</p>
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            Add your first {titleSingular}
          </button>
        </div>
      )}

      {contacts.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>City</th>
              {isVendor && <th>Tax ID</th>}
              <th>Status</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((c) => (
              <ContactRow
                key={c.id}
                contact={c}
                companyId={companyId}
                isVendor={isVendor}
                onEdit={() => setEditing(c)}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ContactRow({
  contact,
  companyId,
  isVendor,
  onEdit,
}: {
  contact: Contact;
  companyId: string;
  isVendor: boolean;
  onEdit: () => void;
}) {
  const deactivate = useDeactivateContact(companyId);
  const update = useUpdateContact(companyId);

  return (
    <tr style={{ opacity: contact.isActive ? 1 : 0.6 }}>
      <td style={{ fontWeight: 500 }}>
        {contact.name}
        {isVendor && contact.track1099 && (
          <span className="tag warn" style={{ marginLeft: 8, fontSize: 11 }}>1099</span>
        )}
      </td>
      <td className="muted">{contact.email ?? '—'}</td>
      <td className="muted">{contact.phone ?? '—'}</td>
      <td className="muted">{[contact.city, contact.region].filter(Boolean).join(', ') || '—'}</td>
      {isVendor && <td className="muted">{contact.taxId ?? '—'}</td>}
      <td>
        {contact.isActive ? (
          <span className="tag ok">active</span>
        ) : (
          <span className="tag bad">inactive</span>
        )}
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>
        <button className="btn-secondary" onClick={onEdit} style={{ marginRight: 8 }}>
          Edit
        </button>
        {contact.isActive ? (
          <button
            className="btn-secondary"
            onClick={() => {
              if (confirm(`Deactivate "${contact.name}"?`)) deactivate.mutate(contact.id);
            }}
            disabled={deactivate.isPending}
          >
            Deactivate
          </button>
        ) : (
          <button
            className="btn-secondary"
            onClick={() => update.mutate({ id: contact.id, input: { isActive: true } })}
            disabled={update.isPending}
          >
            Reactivate
          </button>
        )}
      </td>
    </tr>
  );
}

function ContactForm({
  companyId,
  contactType,
  titleSingular,
  isVendor,
  existing,
  onClose,
}: {
  companyId: string;
  contactType: ContactType;
  titleSingular: string;
  isVendor: boolean;
  existing?: Contact;
  onClose: () => void;
}) {
  const create = useCreateContact(companyId);
  const update = useUpdateContact(companyId);
  const isEditing = !!existing;

  const [form, setForm] = useState({
    name: existing?.name ?? '',
    email: existing?.email ?? '',
    phone: existing?.phone ?? '',
    addressLine1: existing?.addressLine1 ?? '',
    addressLine2: existing?.addressLine2 ?? '',
    city: existing?.city ?? '',
    region: existing?.region ?? '',
    postalCode: existing?.postalCode ?? '',
    countryCode: existing?.countryCode ?? '',
    taxId: existing?.taxId ?? '',
    website: existing?.website ?? '',
    notes: existing?.notes ?? '',
    paymentTerms: existing?.paymentTerms ?? '',
    track1099: existing?.track1099 ?? false,
    taxClassification: (existing?.taxClassification ?? '') as TaxClassification | '',
  });

  const isCustomer = contactType === 'customer';

  const mutation = isEditing ? update : create;
  const errorMessage =
    mutation.error instanceof ApiError
      ? (mutation.error.body as { error?: string } | null)?.error ?? 'Request failed'
      : mutation.error?.message;

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const buildInput = (): CreateContactInput => ({
    name: form.name.trim(),
    contactType,
    email: form.email.trim() || undefined,
    phone: form.phone.trim() || undefined,
    addressLine1: form.addressLine1.trim() || undefined,
    addressLine2: form.addressLine2.trim() || undefined,
    city: form.city.trim() || undefined,
    region: form.region.trim() || undefined,
    postalCode: form.postalCode.trim() || undefined,
    countryCode: form.countryCode.trim().toUpperCase() || undefined,
    taxId: isVendor ? form.taxId.trim() || undefined : undefined,
    website: form.website.trim() || undefined,
    notes: form.notes.trim() || undefined,
    paymentTerms: isCustomer ? form.paymentTerms || undefined : undefined,
    track1099: isVendor ? form.track1099 : false,
    taxClassification: isVendor && form.taxClassification !== '' ? form.taxClassification : null,
  });

  return (
    <form
      className="card"
      style={{ marginBottom: 16 }}
      onSubmit={(e) => {
        e.preventDefault();
        const input = buildInput();
        if (isEditing) {
          update.mutate({ id: existing.id, input }, { onSuccess: () => onClose() });
        } else {
          create.mutate(input, { onSuccess: () => onClose() });
        }
      }}
    >
      <h2 style={{ marginTop: 0, fontSize: 18 }}>
        {isEditing ? `Edit "${existing.name}"` : `New ${titleSingular}`}
      </h2>

      <div className="form-grid">
        <label htmlFor="c-name">Name *</label>
        <input id="c-name" value={form.name} onChange={(e) => set('name', e.target.value)} required autoFocus />

        <label htmlFor="c-email">Email</label>
        <input id="c-email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />

        <label htmlFor="c-phone">Phone</label>
        <input id="c-phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} />

        <label htmlFor="c-addr1">Address line 1</label>
        <input id="c-addr1" value={form.addressLine1} onChange={(e) => set('addressLine1', e.target.value)} />

        <label htmlFor="c-addr2">Address line 2</label>
        <input id="c-addr2" value={form.addressLine2} onChange={(e) => set('addressLine2', e.target.value)} />

        <label htmlFor="c-city">City</label>
        <input id="c-city" value={form.city} onChange={(e) => set('city', e.target.value)} />

        <label htmlFor="c-region">State / region</label>
        <input id="c-region" value={form.region} onChange={(e) => set('region', e.target.value)} maxLength={100} style={{ maxWidth: 200 }} />

        <label htmlFor="c-postal">Postal code</label>
        <input id="c-postal" value={form.postalCode} onChange={(e) => set('postalCode', e.target.value)} maxLength={20} style={{ maxWidth: 200 }} />

        <label htmlFor="c-country">Country (2-letter)</label>
        <input id="c-country" value={form.countryCode} onChange={(e) => set('countryCode', e.target.value.toUpperCase())} maxLength={2} style={{ maxWidth: 80, textTransform: 'uppercase' }} placeholder="US" />

        {isCustomer && (
          <>
            <label htmlFor="c-terms">Payment terms</label>
            <select id="c-terms" value={form.paymentTerms} onChange={(e) => set('paymentTerms', e.target.value)} style={{ maxWidth: 200 }}>
              <option value="">— none —</option>
              {form.paymentTerms !== '' && !(PAYMENT_TERMS as readonly string[]).includes(form.paymentTerms) && (
                <option value={form.paymentTerms}>{form.paymentTerms}</option>
              )}
              {PAYMENT_TERMS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </>
        )}

        {isVendor && (
          <>
            <label htmlFor="c-tax">Tax ID (TIN/EIN)</label>
            <input id="c-tax" value={form.taxId} onChange={(e) => set('taxId', e.target.value)} maxLength={50} placeholder="12-3456789" />

            <label htmlFor="c-1099">1099-NEC</label>
            <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center', fontWeight: 400 }}>
              <input id="c-1099" type="checkbox" checked={form.track1099} onChange={(e) => set('track1099', e.target.checked)} />
              Track this vendor for 1099-NEC
            </label>

            <label htmlFor="c-taxclass">Tax classification</label>
            <select id="c-taxclass" value={form.taxClassification} onChange={(e) => set('taxClassification', e.target.value as TaxClassification | '')}>
              <option value="">—</option>
              {TAX_CLASSIFICATIONS.map((c) => (
                <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </>
        )}

        <label htmlFor="c-web">Website</label>
        <input id="c-web" value={form.website} onChange={(e) => set('website', e.target.value)} placeholder="https://" />

        <label htmlFor="c-notes">Notes</label>
        <textarea id="c-notes" value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={3} />
      </div>

      {errorMessage && <div className="field-error" style={{ marginTop: 12 }}>{errorMessage}</div>}

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button type="submit" className="btn-primary" disabled={mutation.isPending || !form.name.trim()}>
          {mutation.isPending ? (isEditing ? 'Saving…' : 'Creating…') : isEditing ? 'Save changes' : `Create ${titleSingular}`}
        </button>
        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
      </div>
    </form>
  );
}
